"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useScrollSpring = useScrollSpring;
exports.useSmoothTransform = useSmoothTransform;
var _react = require("react");
const DEFAULT_CONFIG = {
  stiffness: 0.1,
  damping: 0.8,
  precision: 0.01,
  mass: 1
};
const PRESETS = {
  default: {
    stiffness: 0.1,
    damping: 0.8
  },
  /* Default spring configuration */
  gentle: {
    stiffness: 0.05,
    damping: 0.7
  },
  /* Soft and Gentle */
  slow: {
    stiffness: 0.02,
    damping: 0.9
  },
  /* Steady Transition */
  bounce: {
    stiffness: 0.1,
    damping: 0.3
  } /* Bouncy Effect */
};

/**
 * Custom hook that creates a spring animation effect for a given target value
 *
 * @param {number} targetValue The target value to which the spring should animate
 * @param {Object} [config={}] Configuration object for the spring animation
 * @param {string} [config.preset] Preset name for the spring configuration
 * @param {number} [config.stiffness=0.1] Stiffness of the spring
 * @param {number} [config.damping=0.8] Damping of the spring
 * @param {number} [config.mass=1] Mass of the spring
 * @param {number} [config.precision=0.01] Precision threshold for stopping the animation
 * @returns {number} Current value of the spring animation
 */
function useScrollSpring(targetValue, config = {}) {
  const presetConfig = config.preset && PRESETS[config.preset] ? PRESETS[config.preset] : {};
  const springConfig = {
    ...DEFAULT_CONFIG,
    ...presetConfig,
    ...config
  };
  const [springValue, setSpringValue] = (0, _react.useState)(targetValue || 0);
  const [velocity, setVelocity] = (0, _react.useState)(0);
  (0, _react.useEffect)(() => {
    let animationFrameId;
    let currentValue = springValue;
    let currentVelocity = velocity;
    const updateSpring = () => {
      const displacement = targetValue - currentValue;
      const springForce = displacement * springConfig.stiffness;
      const dampingForce = currentVelocity * springConfig.damping;
      const acceleration = (springForce - dampingForce) / springConfig.mass;
      currentVelocity += acceleration;
      currentValue += currentVelocity;
      if (Math.abs(currentVelocity) > springConfig.precision || Math.abs(displacement) > springConfig.precision) {
        setSpringValue(currentValue);
        setVelocity(currentVelocity);
        animationFrameId = requestAnimationFrame(updateSpring);
      } else {
        setSpringValue(targetValue);
        setVelocity(0);
      }
    };
    animationFrameId = requestAnimationFrame(updateSpring);
    return () => cancelAnimationFrame(animationFrameId);
  }, [targetValue, springConfig.stiffness, springConfig.damping, springConfig.precision, springConfig.mass]);
  return springValue;
}

/**
 * Applies a smooth transformation to a given transform value based on a specified condition
 *
 * @param {number} clampedTransform Initial transform value that may be clamped
 * @param {boolean} smooth Boolean indicating whether to apply a smooth transformation
 * @param {string} [preset='default'] Preset name for the spring configuration
 * @returns {number} Transformed value, either smoothed or the original clamped value
 */
function useSmoothTransform(clampedTransform, smooth, preset = 'default') {
  const smoothScrollTransform = useScrollSpring(clampedTransform, {
    preset: preset
  });
  return smooth ? smoothScrollTransform : clampedTransform;
}